import React, { useState, useEffect, useMemo, useRef, useCallback } from "react"
import PropTypes from "prop-types"
import { Link } from "react-router-dom"
import { useDispatch } from "react-redux"
import useComposer from "hooks/useComposer"
import {
  getContentStatsById,
  getContentById,
  updateContentByIdAction,
  scoreEmotion,
  scoreBias,
  scoreCognitiveBias,
  scorePoliticalBias,
  scoreFakeness,
  scoreHeadline,
  updateContentById,
  scoreEmpathy,
  scoreEngagement,
  scoreSentiment,
  scoreHateSpeech,
  scoreRacismSexism,
  scoreToxicity,
  scorePatronize,
  scoreSubjectiveBias,
  scoreGenderBias,
  reportBias,
  scoreInformativeness,
  scoreComplexity,
} from "store/actions"
import {
  EditorState,
  convertToRaw,
  convertFromHTML,
  ContentState,
} from "draft-js"
import { stateToHTML } from "draft-js-export-html"
import { Button, CardTitle } from "reactstrap"
import CEditor from "pages/Composer/MyEditor/Editor"
import TabContentComponent from "components/Tabs/TabContentComponent"
import SidebarScore from "components/Score/SidebarScore"
import _ from "lodash"
import { useSelector } from "react-redux"
import {
  getAverageLexicalDiversityScore,
  getAverageReadabilityScore,
} from "utils"
import Project from "pages/Composer/Project/index--old"
import StackedExample from "./progress-bar"
import StackedProgressBar from "components/progress-bar/StackedProgressBar"

const EditModeComposer = ({ project, content }) => {
  const dispatch = useDispatch()
  const [lastUpdated, setLastUpdated] = useState(null)
  const [isRefreshDisabled, setIsRefreshDisabled] = useState(false)

  const { contentsProject, contentStatsProject, isLoading } = useSelector(
    state => state.projects
  )

  const composer = useComposer()
  const {
    sidebarOpen,
    documentTitle,
    editorState,
    hasContent,
    formDataRef,
    activeCard,
    collapse,
    loading,
    toggleSidebar,
    onEditorStateChange,
    handleTitleChange,
    setEditorState,
    setDocumentTitle,
    biasScoreAverage,
    cognitiveBiasScoreAverage,
    politicalBiasScoreAverage,
    fakenessScoreAverage,
    headlineScoreAverage,
    headlineUpdatedScore,
    emotionScore,
    fakenessScore,
    biasScore,
    cognitiveBiasScore,
    politicalBiasScore,
    empathyScore,
    engagementScore,
    sentimentScore,
    hateSpeechScore,
    racismSexismScore,
    toxicityScore,
    patronizeScore,
    subjectiveBiasScore,
    genderBiasScore,
    reportBiasScore,
    informativenessScore,
    complexityScore,
    error,
  } = composer

  const countWords = str => str.split(/\s+/).filter(Boolean).length

  useEffect(() => {
    const htmlContent =
      typeof contentsProject?.content === "string"
        ? contentsProject?.content.trim()
        : ""
    const blocksFromHTML = convertFromHTML(htmlContent)
    const contentState = ContentState.createFromBlockArray(blocksFromHTML)

    if (contentsProject) {
      setEditorState(EditorState.createWithContent(contentState))
      setDocumentTitle(contentsProject.title)
    }

    const formContentData = new FormData()

    formContentData.append("content", htmlContent)
    const htmlContents = formContentData.get("content")
    if (countWords(htmlContents) >= 100) {
      const debounceTimeout = setTimeout(() => {
        dispatch(scoreEmotion(formContentData))
        dispatch(scoreBias(formContentData))
        dispatch(scoreCognitiveBias(formContentData))
        dispatch(scorePoliticalBias(formContentData))
        dispatch(scoreFakeness(formContentData))
        dispatch(scoreHeadline(formContentData))

        dispatch(scoreEmpathy(formContentData))
        dispatch(scoreEngagement(formContentData))
        dispatch(scoreSentiment(formContentData))
        dispatch(scoreHateSpeech(formContentData))
        dispatch(scoreRacismSexism(formContentData))
        dispatch(scoreToxicity(formContentData))
        dispatch(scorePatronize(formContentData))
        dispatch(scoreSubjectiveBias(formContentData))
        dispatch(scoreGenderBias(formContentData))
        dispatch(reportBias(formContentData))
        dispatch(scoreInformativeness(formContentData))
        dispatch(scoreComplexity(formContentData))

        dispatch(getContentStatsById(content, project))
      }, 2000)
      setLastUpdated(new Date())
      return () => clearTimeout(debounceTimeout)
    } else {
      console.log("Not enough words in the document to start analysis")
    }
  }, [contentsProject])

  useEffect(() => {
    dispatch(getContentById(content, project))
  }, [])

  const handleRefreshClick = () => {
    if (isRefreshDisabled) return

    setIsRefreshDisabled(true)

    const currentContent = editorState.getCurrentContent()
    const htmlContent = stateToHTML(currentContent)

    console.log("htmlContent", htmlContent)

    const formContentData = new FormData()

    formContentData.append("content", htmlContent)
    const htmlContents = formContentData.get("content")
    if (countWords(htmlContents) >= 100) {
      const debounceTimeout = setTimeout(() => {
        dispatch(scoreEmotion(formContentData))
        dispatch(scoreBias(formContentData))
        dispatch(scoreCognitiveBias(formContentData))
        dispatch(scorePoliticalBias(formContentData))
        dispatch(scoreFakeness(formContentData))
        dispatch(scoreHeadline(formContentData))

        dispatch(scoreEmpathy(formContentData))
        dispatch(scoreEngagement(formContentData))
        dispatch(scoreSentiment(formContentData))
        dispatch(scoreHateSpeech(formContentData))
        dispatch(scoreRacismSexism(formContentData))
        dispatch(scoreToxicity(formContentData))
        dispatch(scorePatronize(formContentData))
        dispatch(scoreSubjectiveBias(formContentData))
        dispatch(scoreGenderBias(formContentData))
        dispatch(reportBias(formContentData))

        dispatch(scoreInformativeness(formContentData))
        dispatch(scoreComplexity(formContentData))

        //dispatch(getContentStatsById(content, project))
        //dispatch(getContentById(content, project))
      }, 2000)
      setLastUpdated(new Date())
      return () => clearTimeout(debounceTimeout)
    } else {
      console.log("Not enough words in the document to start analysis")
    }
  }

  useEffect(() => {
    if (formDataRef.current && hasContent) {
      const debounceTimeout = setTimeout(() => {
        dispatch(updateContentByIdAction(project, content, formDataRef.current))
        /* dispatch(getContentById(content, project)) */
        dispatch(getContentStatsById(content, project))
      }, 2000)

      return () => clearTimeout(debounceTimeout)
    }
  }, [dispatch, formDataRef.current, hasContent])

  const statsContentRender = useMemo(() => {
    console.log(
      "stats Content Render ran:" + contentStatsProject?.document_stats
    )
    return contentStatsProject?.document_stats
  }, [contentStatsProject])

  const attributesRender = useMemo(() => {
    console.log("attributes Render ran:" + contentStatsProject?.attributes)
    return contentStatsProject?.attributes
  }, [contentStatsProject])

  const lexicalRichnessScoreAverage =
    getAverageLexicalDiversityScore(statsContentRender)
  const readabilityScore = getAverageReadabilityScore(statsContentRender)

  const overAllReadability = {}

  const handleSave = useCallback(() => {
    const rawContent = convertToRaw(editorState.getCurrentContent())
    let htmlContent = ""

    rawContent.blocks.forEach(block => {
      htmlContent += "<p>" + block.text + "</p>"
    })

    htmlContent = htmlContent.substring(3, htmlContent.length - 4)

    const formData = new FormData()
    formData.append("content", htmlContent)
    formData.append("title", documentTitle)
    dispatch(updateContentById(project, content, formData))
  }, [dispatch, editorState, documentTitle, project, content])

  useEffect(() => {
    let timer
    if (isRefreshDisabled) {
      timer = setTimeout(() => {
        setIsRefreshDisabled(false)
      }, 30000)
    }
    return () => clearTimeout(timer)
  }, [isRefreshDisabled])

  const sidebarProps = {
    isLoading,
    loading,
    overAllReadability,
    readabilityScore,
    lexicalRichnessScoreAverage,
    statsContentRender,
    fakenessScoreAverage,
    fakenessScore,
    headlineScoreAverage,
    headlineUpdatedScore,
    biasScoreAverage,
    biasScore,
    cognitiveBiasScoreAverage,
    cognitiveBiasScore,
    politicalBiasScoreAverage,
    politicalBiasScore,
    emotionScore,
    empathyScore,
    engagementScore,
    sentimentScore,
    hateSpeechScore,
    racismSexismScore,
    toxicityScore,
    patronizeScore,
    subjectiveBiasScore,
    genderBiasScore,
    reportBiasScore,
    informativenessScore,
    complexityScore,
    error,
  }

  const renderModalContent = toggle => {
    // Return the JSX for the modal content. 'toggle' is the function to close the modal.
    return (
      <div>
        <h2>Emotion Details</h2>
        {/* ... Other details to show in the modal */}
      </div>
    )
  }
  function resizeInput(input) {
    const minWidth = input.placeholder.length + "ch"
    input.style.width =
      (input.value.length ? input.value.length : input.placeholder.length) +
      "ch"
    input.style.minWidth = minWidth
  }

  return (
    <React.Fragment>
      <div style={{ flex: 1 }}>
        <CardTitle style={{ position: "relative", textAlign: "start" }}>
          <input
            className="document-title"
            type="text"
            value={documentTitle}
            onChange={e => {
              handleTitleChange(e)
              resizeInput(e.target)
            }}
            onFocus={e => (e.target.style.backgroundColor = "#f0f0f0")}
            onBlur={e => (e.target.style.backgroundColor = "#ffffff")}
            placeholder="Untitled document"
            style={{ width: "auto" }}
            ref={input => input && resizeInput(input)}
          />
          {!sidebarOpen && (
            <Button
              color="primary"
              onClick={toggleSidebar}
              style={{
                position: "absolute",
                right: 0,
                top: "1em",
                borderRadius: "30px",
              }}
            >
              START ASSISTANT
            </Button>
          )}
        </CardTitle>
        {editorState && (
          <CEditor
            onEditorStateChange={onEditorStateChange}
            editorState={editorState}
            hasContent={hasContent}
          />
        )}
        <TabContentComponent
          collapse={collapse}
          attributesRender={attributesRender}
          statsContentRender={statsContentRender}
          isLoading={isLoading}
          loading={loading}
          emotionData={statsContentRender}
        />
        <div className="buttons-composer">
          <div>
            <Link
              to={`/project/documents/list/${project}`}
              style={{ marginRight: "12px" }}
            >
              <button
                className="btn btn-composer composer__project"
                style={{ backgroundColor: "#eee" }}
              >
                <span style={{ fontSize: "20px" }}>Back</span>
              </button>
            </Link>
            <button
              className="btn btn-composer composer__project"
              onClick={handleSave}
              //disabled={!hasContent ? false : true}
            >
              <span style={{ fontSize: "20px" }}> Save</span>
            </button>
          </div>
        </div>
      </div>
      <SidebarScore
        lastUpdated={lastUpdated}
        onRefreshClick={handleRefreshClick}
        isRefreshDisabled={isRefreshDisabled}
        activeCard={activeCard}
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        isLoading={isLoading}
        {...sidebarProps}
      />
    </React.Fragment>
  )
}

EditModeComposer.propTypes = {
  project: PropTypes.string,
  content: PropTypes.string,
}

export default EditModeComposer
